import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Play,
  Pause,
  Check,
  Clock,
  User,
  Calendar,
  Package,
  MapPin,
  FileText,
  RefreshCw,
  AlertTriangle,
  DollarSign,
  MessageSquare,
  ClipboardCheck,
  RotateCcw,
  Send,
  X,
  Plus,
  Minus,
  Search,
  Wrench,
  Paperclip
} from 'lucide-react';
import { workOrderService, WORK_ORDER_STATUSES, WORK_ORDER_TYPES, WORK_ORDER_PRIORITIES } from '../../../services/workOrderService';
import { inventoryService } from '../../../services/inventoryService';
import { authService } from '../../../services/authService';
import { useCmmsAuth } from '../../../contexts/CmmsAuthContext';
import { PERMISSIONS } from '../../../constants/permissions';
import { useDateFormat } from '../../../hooks/useDateFormat';

const WorkOrderDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [workOrder, setWorkOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [noteModal, setNoteModal] = useState(null);
  const [noteValue, setNoteValue] = useState('');
  const { hasPermission } = useCmmsAuth();
  const canCompleteWorkOrders = hasPermission(PERMISSIONS.COMPLETE_WORK_ORDERS);
  const { formatDateTime } = useDateFormat();

  // Parts usage state
  const [showPartsModal, setShowPartsModal] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventorySearch, setInventorySearch] = useState('');
  const [selectedParts, setSelectedParts] = useState([]);
  const [partsSubmitting, setPartsSubmitting] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  useEffect(() => {
    fetchWorkOrder();
  }, [id]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      const data = await workOrderService.getWorkOrderById(id);
      setWorkOrder(data);
    } catch (err) {
      console.error('Failed to load work order:', err);
    } finally {
      setLoading(false);
    }
  };

  const openNoteModal = (config) => {
    setNoteValue('');
    setNoteModal(config);
  };

  const closeNoteModal = () => {
    setNoteModal(null);
    setNoteValue('');
  };

  const handleStatusChange = (newStatus) => {
    const doUpdate = async (notes) => {
      try {
        await workOrderService.updateStatus(id, newStatus, notes || null);
        fetchWorkOrder();
      } catch (err) {
        console.error('Failed to update status:', err);
        alert(err.message || 'Failed to update status');
      }
    };

    if (newStatus === 'PENDING_REVIEW') {
      openNoteModal({
        title: t('cmms.wo_submitForReview'),
        placeholder: t('cmms.wod_placeholderReview'),
        required: true,
        onConfirm: doUpdate,
      });
    } else if (newStatus === 'ON_HOLD') {
      openNoteModal({
        title: t('cmms.wo_putOnHold'),
        placeholder: t('cmms.wod_placeholderHold'),
        required: false,
        onConfirm: doUpdate,
      });
    } else if (newStatus === 'IN_PROGRESS') {
      openNoteModal({
        title: t('cmms.wod_modalStartTitle'),
        placeholder: t('cmms.wod_placeholderStart'),
        required: false,
        onConfirm: doUpdate,
      });
    } else {
      doUpdate(null);
    }
  };

  const handleComplete = () => {
    openNoteModal({
      title: t('cmms.wod_modalCompleteTitle'),
      placeholder: t('cmms.wod_placeholderComplete'),
      required: false,
      onConfirm: async (notes) => {
        try {
          await workOrderService.completeWorkOrder(id, notes || null);
          fetchWorkOrder();
        } catch (err) {
          console.error('Failed to complete:', err);
          alert('Failed to complete work order');
        }
      },
    });
  };

  const handleDelete = async () => {
    if (window.confirm(t('cmms.wod_deleteConfirm'))) {
      try {
        await workOrderService.deleteWorkOrder(id);
        navigate('/portal/cmms/work-orders');
      } catch (err) {
        console.error('Failed to delete:', err);
        alert('Failed to delete work order');
      }
    }
  };

  const openPartsModal = async () => {
    setSelectedParts([]);
    setInventorySearch('');
    try {
      const items = await inventoryService.getInventory();
      setInventoryItems(items || []);
    } catch (err) {
      console.error('Failed to load inventory:', err);
      setInventoryItems([]);
    }
    setShowPartsModal(true);
  };

  const addPartToSelection = (item) => {
    setSelectedParts(prev => {
      const existing = prev.find(p => p.inventoryId === item.id);
      if (existing) return prev;
      return [...prev, { inventoryId: item.id, name: item.name, partNumber: item.sku, quantity: 1, unitCost: item.unitCost || 0, available: item.quantityOnHand }];
    });
  };

  const removePartFromSelection = (inventoryId) => {
    setSelectedParts(prev => prev.filter(p => p.inventoryId !== inventoryId));
  };

  const updatePartQty = (inventoryId, qty) => {
    setSelectedParts(prev => prev.map(p =>
      p.inventoryId === inventoryId ? { ...p, quantity: Math.max(1, Math.min(qty, p.available)) } : p
    ));
  };

  const handleSubmitParts = async () => {
    if (selectedParts.length === 0) return;
    setPartsSubmitting(true);
    try {
      await workOrderService.useParts(id, selectedParts.map(p => ({
        inventoryId: p.inventoryId,
        name: p.name,
        partNumber: p.partNumber,
        quantity: p.quantity,
        unitCost: p.unitCost,
        totalCost: (p.unitCost || 0) * p.quantity
      })));
      setShowPartsModal(false);
      fetchWorkOrder();
    } catch (err) {
      console.error('Failed to log parts:', err);
      alert(err.message || 'Failed to log parts usage');
    } finally {
      setPartsSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      OPEN: 'bg-gray-100 text-gray-800',
      ASSIGNED: 'bg-blue-100 text-blue-800',
      IN_PROGRESS: 'bg-yellow-100 text-yellow-800',
      ON_HOLD: 'bg-orange-100 text-orange-800',
      PENDING_REVIEW: 'bg-purple-100 text-purple-800',
      COMPLETED: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      URGENT: 'bg-red-100 text-red-800 border-red-200',
      HIGH: 'bg-orange-100 text-orange-800 border-orange-200',
      MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      LOW: 'bg-green-100 text-green-800 border-green-200',
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{t('cmms.wod_notFound')}</p>
        <Link to="/portal/cmms/work-orders" className="text-primary-600 hover:underline mt-2 inline-block">
          {t('cmms.wod_backToWorkOrders')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/portal/cmms/work-orders')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{workOrder.woNumber}</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(workOrder.status)}`}>
                {workOrder.status?.replace('_', ' ')}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(workOrder.priority)}`}>
                {workOrder.priority}
              </span>
            </div>
            <p className="text-gray-600 mt-1">{workOrder.title}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {workOrder.status !== 'COMPLETED' && workOrder.status !== 'CANCELLED' && (
            <>
              {workOrder.status !== 'IN_PROGRESS' && (
                <button
                  onClick={() => handleStatusChange('IN_PROGRESS')}
                  className="btn btn-primary"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {t('cmms.wo_startWork')}
                </button>
              )}
              {workOrder.status === 'IN_PROGRESS' && (
                <>
                  <button
                    onClick={openPartsModal}
                    className="btn btn-outline text-blue-700 border-blue-300 hover:bg-blue-50"
                  >
                    <Wrench className="w-4 h-4 mr-2" />
                    {t('cmms.wod_logPartsUsed')}
                  </button>
                  <button
                    onClick={() => handleStatusChange('ON_HOLD')}
                    className="btn btn-outline"
                  >
                    <Pause className="w-4 h-4 mr-2" />
                    {t('cmms.wod_hold')}
                  </button>
                  {canCompleteWorkOrders ? (
                    <button
                      onClick={handleComplete}
                      className="btn btn-primary bg-green-600 hover:bg-green-700"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {t('cmms.wod_complete')}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleStatusChange('PENDING_REVIEW')}
                      className="btn btn-primary bg-purple-600 hover:bg-purple-700"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {t('cmms.wo_submitForReview')}
                    </button>
                  )}
                </>
              )}
              {workOrder.status === 'ON_HOLD' && (
                <button
                  onClick={() => handleStatusChange('IN_PROGRESS')}
                  className="btn btn-primary"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {t('cmms.wo_resume')}
                </button>
              )}
            </>
          )}
          <button onClick={handleDelete} className="btn btn-outline text-red-600 border-red-300 hover:bg-red-50">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Pending Review Alert - Prominently displayed for managers/supervisors */}
      {workOrder.status === 'PENDING_REVIEW' && (
        <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <ClipboardCheck className="w-8 h-8 text-purple-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-purple-900 mb-2">{t('cmms.pendingReview')}</h2>
              <p className="text-purple-700 mb-4">
                {t('cmms.wod_pendingReviewDesc')}
              </p>
              {workOrder.completionNotes && (
                <div className="bg-white rounded-lg p-4 border border-purple-200 mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">{t('cmms.wod_techFeedback')}</h3>
                  <div className="text-gray-600 whitespace-pre-wrap">{workOrder.completionNotes}</div>
                </div>
              )}
              {canCompleteWorkOrders && (
                <div className="flex gap-3">
                  <button
                    onClick={handleComplete}
                    className="btn bg-green-600 text-white hover:bg-green-700 px-4 py-2 rounded-lg flex items-center"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {t('cmms.wo_approveComplete')}
                  </button>
                  <button
                    onClick={() => handleStatusChange('IN_PROGRESS')}
                    className="btn bg-orange-500 text-white hover:bg-orange-600 px-4 py-2 rounded-lg flex items-center"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {t('cmms.wo_returnForRework')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">{t('common.description')}</h2>
            <p className="text-gray-600">{workOrder.description || t('cmms.wod_noDescription')}</p>
          </div>

          {/* Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">{t('cmms.wod_details')}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <InfoItem icon={Package} label={t('cmms.wo_asset')} value={workOrder.assetName} link={workOrder.assetId ? `/portal/cmms/assets/${workOrder.assetId}` : null} />
              <InfoItem icon={MapPin} label={t('cmms.asset_location')} value={workOrder.locationName} />
              <InfoItem icon={FileText} label={t('common.type')} value={workOrder.type} />
              <InfoItem icon={User} label={t('cmms.wo_assignedTo')} value={workOrder.assignedToName} />
              <InfoItem icon={User} label={t('cmms.wod_createdBy')} value={workOrder.createdByName} />
              <InfoItem icon={Calendar} label={t('common.created')} value={workOrder.createdAt ? formatDateTime(workOrder.createdAt) : null} />
            </div>
          </div>

          {/* Scheduling */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">{t('cmms.wod_scheduling')}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <InfoItem icon={Calendar} label={t('cmms.wo_scheduledStart')} value={workOrder.scheduledStart ? formatDateTime(workOrder.scheduledStart) : null} />
              <InfoItem icon={Calendar} label={t('cmms.wo_scheduledEnd')} value={workOrder.scheduledEnd ? formatDateTime(workOrder.scheduledEnd) : null} />
              <InfoItem icon={Clock} label={t('cmms.wod_estimatedHours')} value={workOrder.estimatedHours} />
              <InfoItem icon={Calendar} label={t('cmms.wod_actualStart')} value={workOrder.actualStart ? formatDateTime(workOrder.actualStart) : null} />
              <InfoItem icon={Calendar} label={t('cmms.wod_actualEnd')} value={workOrder.actualEnd ? formatDateTime(workOrder.actualEnd) : null} />
              <InfoItem icon={Clock} label={t('cmms.wod_actualHours')} value={workOrder.actualHours} />
            </div>
          </div>

          {/* Notes & Activity - Don't show if PENDING_REVIEW (already shown in alert) */}
          {workOrder.completionNotes && workOrder.status !== 'PENDING_REVIEW' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4">{t('cmms.wod_notesActivity')}</h2>
              <div className="text-gray-600 whitespace-pre-wrap">{workOrder.completionNotes}</div>
            </div>
          )}

          {/* Parts Used */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Wrench className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold">{t('cmms.wod_partsUsedSection')}</h2>
            </div>
            {workOrder.partsUsed && workOrder.partsUsed.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs text-gray-500 uppercase">
                      <th className="pb-2 pr-4">{t('common.name')}</th>
                      <th className="pb-2 pr-4">{t('cmms.wod_partNumber')}</th>
                      <th className="pb-2 pr-4 text-right">{t('cmms.wod_qty')}</th>
                      <th className="pb-2 pr-4 text-right">{t('cmms.wod_unitCost')}</th>
                      <th className="pb-2 text-right">{t('cmms.wod_totalCost')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {workOrder.partsUsed.map((part, idx) => (
                      <tr key={idx}>
                        <td className="py-2 pr-4 font-medium text-gray-900">{part.name || '-'}</td>
                        <td className="py-2 pr-4 text-gray-500">{part.partNumber || '-'}</td>
                        <td className="py-2 pr-4 text-right">{part.quantity}</td>
                        <td className="py-2 pr-4 text-right">{part.unitCost != null ? `$${Number(part.unitCost).toFixed(2)}` : '-'}</td>
                        <td className="py-2 text-right font-semibold text-gray-900">{part.totalCost != null ? `$${Number(part.totalCost).toFixed(2)}` : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-gray-200">
                      <td colSpan={4} className="pt-3 text-right text-sm font-semibold text-gray-700">{t('cmms.wod_totalPartsCost')}</td>
                      <td className="pt-3 text-right text-sm font-bold text-primary-600">
                        ${workOrder.partsUsed.reduce((s, p) => s + (p.totalCost || 0), 0).toFixed(2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">{t('cmms.wod_noPartsUsed')}</p>
            )}
          </div>

          {/* Attachments */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Paperclip className="w-5 h-5 text-gray-400" />
              <h2 className="text-lg font-semibold">{t('cmms.wod_attachments')}</h2>
            </div>
            {workOrder.attachments && workOrder.attachments.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {workOrder.attachments.map(att => {
                  const url = `/api/cmms/work-orders/${id}/attachments/${att.id}/file?companyId=${authService.getCompanyId()}`;
                  return (
                    <div key={att.id} className="flex flex-col items-center">
                      <button
                        type="button"
                        onClick={() => setLightboxUrl(url)}
                        className="rounded-lg overflow-hidden border border-gray-200 hover:border-primary-400 transition-colors focus:outline-none"
                      >
                        <img
                          src={url}
                          alt={att.fileName}
                          className="w-24 h-24 object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </button>
                      <span className="mt-1 text-xs text-gray-500 truncate w-24 text-center">{att.fileName}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">{t('cmms.wod_noAttachments')}</p>
            )}
          </div>

          {/* Completion Info */}
          {workOrder.status === 'COMPLETED' && workOrder.completedBy && (
            <div className="bg-green-50 rounded-xl border border-green-200 p-6">
              <h2 className="text-lg font-semibold mb-2 text-green-800">{t('cmms.wo_completed')}</h2>
              <div className="text-sm text-green-700">
                <span className="font-medium">{t('cmms.wod_completedBy')}</span> {workOrder.completedByName || 'Unknown'}
                {workOrder.completedAt && (
                  <span> {t('cmms.wod_on')} {formatDateTime(workOrder.completedAt)}</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status Timeline */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">{t('common.status')}</h2>
            <div className="space-y-3">
              {WORK_ORDER_STATUSES.filter(s => s.value !== 'CANCELLED').map((status) => {
                const isActive = workOrder.status === status.value;
                const isPast = getStatusOrder(workOrder.status) > getStatusOrder(status.value);
                return (
                  <div key={status.value} className={`flex items-center gap-3 ${isActive ? 'font-medium' : ''}`}>
                    <div className={`w-3 h-3 rounded-full ${
                      isActive ? 'bg-primary-600' :
                      isPast ? 'bg-green-500' :
                      'bg-gray-200'
                    }`} />
                    <span className={isPast || isActive ? 'text-gray-900' : 'text-gray-400'}>
                      {status.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Costs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">{t('cmms.wod_costs')}</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cmms.wod_estimated')}</span>
                <span className="font-medium">{workOrder.estimatedCost ? `$${workOrder.estimatedCost.toLocaleString()}` : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cmms.wod_labor')}</span>
                <span className="font-medium">{workOrder.actualLaborCost ? `$${workOrder.actualLaborCost.toLocaleString()}` : '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('cmms.wod_parts')}</span>
                <span className="font-medium">{workOrder.actualPartsCost ? `$${workOrder.actualPartsCost.toLocaleString()}` : '-'}</span>
              </div>
              <div className="flex justify-between pt-3 border-t border-gray-100">
                <span className="text-gray-900 font-medium">{t('common.total')}</span>
                <span className="font-bold text-primary-600">{workOrder.actualTotalCost ? `$${workOrder.actualTotalCost.toLocaleString()}` : '-'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 p-2"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={lightboxUrl}
            alt="Attachment"
            className="max-w-full max-h-full rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Parts Usage Modal */}
      {showPartsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">{t('cmms.wod_logPartsTitle')}</h2>
              <button onClick={() => setShowPartsModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              {/* Search inventory */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.wod_searchInventory')}</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={inventorySearch}
                    onChange={e => setInventorySearch(e.target.value)}
                    placeholder={t('cmms.wod_searchBySKU')}
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                {/* Inventory list */}
                <div className="mt-2 border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                  {inventoryItems
                    .filter(item => {
                      const q = inventorySearch.toLowerCase();
                      return !q || item.name?.toLowerCase().includes(q) || item.sku?.toLowerCase().includes(q);
                    })
                    .filter(item => item.quantityOnHand > 0)
                    .map(item => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => addPartToSelection(item)}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-blue-50 text-left border-b border-gray-100 last:border-0"
                      >
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          <div className="text-xs text-gray-500">{item.sku} · {t('cmms.wod_qty')}: {item.quantityOnHand} {item.unitOfMeasure || ''}</div>
                        </div>
                        <div className="text-sm font-medium text-gray-700">${(item.unitCost || 0).toFixed(2)}</div>
                      </button>
                    ))}
                  {inventoryItems.filter(i => i.quantityOnHand > 0).length === 0 && (
                    <div className="text-center text-sm text-gray-500 py-4">{t('cmms.wod_noStock')}</div>
                  )}
                </div>
              </div>

              {/* Selected parts */}
              {selectedParts.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">{t('cmms.wod_selectedParts')}</div>
                  <div className="space-y-2">
                    {selectedParts.map(part => (
                      <div key={part.inventoryId} className="flex items-center gap-3 bg-blue-50 rounded-lg p-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">{part.name}</div>
                          <div className="text-xs text-gray-500">${(part.unitCost || 0).toFixed(2)} {t('cmms.wod_perUnit')}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button type="button" onClick={() => updatePartQty(part.inventoryId, part.quantity - 1)} className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100">
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center text-sm font-medium">{part.quantity}</span>
                          <button type="button" onClick={() => updatePartQty(part.inventoryId, part.quantity + 1)} className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100">
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <div className="text-sm font-semibold text-gray-900 w-16 text-right">
                          ${((part.unitCost || 0) * part.quantity).toFixed(2)}
                        </div>
                        <button type="button" onClick={() => removePartFromSelection(part.inventoryId)} className="text-red-400 hover:text-red-600">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 border-t border-blue-200 text-sm font-semibold">
                      <span>{t('cmms.wod_totalPartsCost')}</span>
                      <span className="text-primary-600">
                        ${selectedParts.reduce((s, p) => s + (p.unitCost || 0) * p.quantity, 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button type="button" onClick={() => setShowPartsModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSubmitParts}
                disabled={selectedParts.length === 0 || partsSubmitting}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {partsSubmitting ? t('cmms.wod_saving') : t('cmms.wod_logCount', { count: selectedParts.length })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Note Modal — replaces window.prompt() for status-change notes */}
      {noteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">{noteModal.title}</h2>
              <button onClick={closeNoteModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">{t('common.notes')}</label>
                  {noteModal.required ? (
                    <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded">{t('common.required')}</span>
                  ) : (
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{t('common.optional')}</span>
                  )}
                </div>
                <textarea
                  rows={4}
                  value={noteValue}
                  onChange={(e) => setNoteValue(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 resize-none"
                  placeholder={noteModal.placeholder}
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeNoteModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (noteModal.required && !noteValue.trim()) return;
                    const val = noteValue.trim() || null;
                    const onConfirm = noteModal.onConfirm;
                    closeNoteModal();
                    onConfirm(val);
                  }}
                  disabled={noteModal.required && !noteValue.trim()}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const getStatusOrder = (status) => {
  const order = { OPEN: 1, ASSIGNED: 2, IN_PROGRESS: 3, ON_HOLD: 3.5, PENDING_REVIEW: 3.8, COMPLETED: 4, CANCELLED: 0 };
  return order[status] || 0;
};

const InfoItem = ({ icon: Icon, label, value, link }) => {
  if (!value) return null;
  const content = (
    <div className="flex items-start gap-3">
      <Icon className="w-5 h-5 text-gray-400 mt-0.5" />
      <div>
        <div className="text-sm text-gray-500">{label}</div>
        <div className={`font-medium ${link ? 'text-primary-600 hover:underline' : 'text-gray-900'}`}>{value}</div>
      </div>
    </div>
  );
  return link ? <Link to={link}>{content}</Link> : content;
};

export default WorkOrderDetail;
